import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Alert,
  FormControlLabel,
  Divider,
} from "@mui/material";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuKullaniciYetkilerPage.css";

export default function KuyumcuKullaniciYetkilerPage() {
  const navigate = useNavigate();
  const { id: hedefKullaniciId } = useParams();
  const location = useLocation();

  const girisYapanUserId = getUserId();

  // liste sayfasından state ile taşıyoruz; yoksa boş da kalabilir
  const hedefKullaniciAdi = location.state?.kullaniciAdi || "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [tumYetkiler, setTumYetkiler] = useState([]); // yetkiler tablosu
  const [seciliYetkiIdler, setSeciliYetkiIdler] = useState([]); // ekranda seçili olanlar
  const [initialYetkiIdler, setInitialYetkiIdler] = useState([]); // DB'den gelen ilk hal (kıyas için)

  const [search, setSearch] = useState("");

  const filteredYetkiler = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return tumYetkiler;
    return tumYetkiler.filter((y) =>
      String(y.ad || y.yetkiAdi || "").toLowerCase().includes(q)
    );
  }, [search, tumYetkiler]);

    // Arama filtresine göre ekranda görünen yetki id'leri
    const filteredYetkiIdler = useMemo(
      () => filteredYetkiler.map((y) => y.id),
      [filteredYetkiler]
    );

    // Görünenlerin hepsi seçili mi?
    const isAllFilteredSelected = useMemo(() => {
      if (filteredYetkiIdler.length === 0) return false;
      const s = new Set(seciliYetkiIdler);
      return filteredYetkiIdler.every((id) => s.has(id));
    }, [filteredYetkiIdler, seciliYetkiIdler]);

    // Kısmi seçim var mı? (checkbox indeterminate için)
    const isSomeFilteredSelected = useMemo(() => {
      if (filteredYetkiIdler.length === 0) return false;
      const s = new Set(seciliYetkiIdler);
      const selectedCount = filteredYetkiIdler.filter((id) => s.has(id)).length;
      return selectedCount > 0 && selectedCount < filteredYetkiIdler.length;
    }, [filteredYetkiIdler, seciliYetkiIdler]);


  // DB'deki seçim ile ekrandaki seçim farklı mı? (Kaydet butonu buna göre görünsün)
  const hasChanges = useMemo(() => {
    const a = new Set(initialYetkiIdler || []);
    const b = new Set(seciliYetkiIdler || []);

    if (a.size !== b.size) return true;
    for (const x of a) {
      if (!b.has(x)) return true;
    }
    return false;
  }, [initialYetkiIdler, seciliYetkiIdler]);

  const toggleYetki = (yetkiId) => {
    setSeciliYetkiIdler((prev) =>
      prev.includes(yetkiId) ? prev.filter((x) => x !== yetkiId) : [...prev, yetkiId]
    );
  };

  // Arama filtresine göre görünen yetkilerin tamamını seç / kaldır
  const toggleAllFiltered = () => {
    setSeciliYetkiIdler((prev) => {
      const prevSet = new Set(prev);
      const allSelected = filteredYetkiIdler.every((id) => prevSet.has(id));

      // Hepsi seçiliyse -> görünenleri kaldır
      if (allSelected) {
        return prev.filter((id) => !filteredYetkiIdler.includes(id));
      }

      // Değilse -> görünenlerin hepsini ekle (unique)
      filteredYetkiIdler.forEach((id) => prevSet.add(id));
      return Array.from(prevSet);
    });
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // 1) tüm yetkiler
      const yetkiResp = await axiosInstance.get("/yetkiler", {
        params: { girisYapanUserId },
      });

      // 2) kullanıcının yetkileri
      const userYetkiResp = await axiosInstance.get("/kullanici-yetkiler", {
        params: { girisYapanUserId, kullaniciId: hedefKullaniciId },
      });

      const all = yetkiResp.data || [];
      const userList = userYetkiResp.data || [];

      // userList response şekli:
      // - Backend List<Long> dönerse: [1,2,3]
      // - Eski yapı dönerse: [{ yetkiId: 1 }, ...]
      const selected =
        Array.isArray(userList) && typeof userList[0] === "number"
          ? userList
          : userList.map((x) => x.yetkiId);

      setTumYetkiler(all);
      setSeciliYetkiIdler(selected);
      setInitialYetkiIdler(selected); // DB'den gelen ilk hal
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Yetkiler yüklenemedi."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hedefKullaniciId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hedefKullaniciId]);

  const kaydet = async () => {
    setError("");
    try {
      // Profesyonel yöntem: replace (sil + yeniden ekle)
      await axiosInstance.post(
        "/kullanici-yetkiler/replace",
        {
          kullaniciId: Number(hedefKullaniciId),
          yetkiIdler: seciliYetkiIdler,
        },
        { params: { girisYapanUserId } }
      );

      // Kaydet başarılı -> yeni durum artık referans olsun (Kaydet butonu kaybolsun)
      setInitialYetkiIdler(seciliYetkiIdler);

      alert("Yetkiler kaydedildi.");
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Yetki kaydetme başarısız."
      );
    }
  };

  return (
    <div className="kuyumcu-main-container">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />

      <div className="kuyumcu-page-content">
        <div className="kky-baslik-bar">
          <div className="kky-baslik-sol">
            <div className="kky-baslik-ust">Yetki Yönetimi</div>
            <div className="kky-baslik-alt">
              {hedefKullaniciAdi
                ? `Kullanıcı: ${hedefKullaniciAdi}`
                : `Kullanıcı ID: ${hedefKullaniciId}`}
            </div>
          </div>

          <div className="kky-baslik-sag">

            {/* Sadece değişiklik varsa Kaydet görünsün */}
            {hasChanges && (
              <Button
                variant="contained"
                className="kky-kaydet-btn"
                onClick={kaydet}
                disabled={loading}
              >
                Kaydet
              </Button>
            )}

            <Button
              variant="outlined"
              className="kky-geri-btn"
              onClick={() => navigate(-1)}
            >
              Geri
            </Button>

          </div>
        </div>

        {error && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper className="kky-card">
              <Box className="kky-toolbar">
                <Typography className="kky-toolbar-title">
                  Yetkiler ({seciliYetkiIdler.length} seçili)
                </Typography>

                <TextField
                  className="kky-search"
                  size="small"
                  placeholder="Yetki ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <FormControlLabel
                  sx={{ ml: 1 }}
                  control={
                    <Checkbox
                      checked={isAllFilteredSelected}
                      indeterminate={isSomeFilteredSelected}
                      disabled={filteredYetkiIdler.length === 0}
                      onChange={toggleAllFiltered}
                      sx={{
                        color: "#b8860b",
                        "&.Mui-checked": { color: "#b8860b" },
                      }}
                    />
                  }
                  label={isAllFilteredSelected ? "Tümünü Kaldır" : "Tümü Seç"}
                />

              </Box>

              <Divider sx={{ my: 1 }} />

              <div className="kky-list">
                {filteredYetkiler.map((y) => {
                  const id = y.id;
                  const ad = y.ad || y.yetkiAdi || `Yetki #${id}`;

                  return (
                    <FormControlLabel
                      key={id}
                      className="kky-item"
                      control={
                        <Checkbox
                          checked={seciliYetkiIdler.includes(id)}
                          onChange={() => toggleYetki(id)}
                          sx={{
                            color: "#b8860b",
                            "&.Mui-checked": { color: "#b8860b" },
                          }}
                        />
                      }
                      label={ad}
                    />
                  );
                })}
              </div>
            </Paper>
          )}
        </Box>
      </div>
    </div>
  );
}
